import React, { useEffect, useState } from 'react';
import Button from '../../components/elements/button';
import { Search, ExportIcon } from '../../assets/icons';
import '../../assets/css/admin/farmers.css';
import LoadingSpinner from '../../components/elements/LoadingSpinner';
import { usersApi } from '../../network/apis/users';

const ROLE_OPTIONS = [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Admins' },
    { value: 'doctor', label: 'Doctors' },
    { value: 'laboratory', label: 'Labs' },
    { value: 'vendor', label: 'Vendors' },
    { value: 'aht', label: 'AHTs' },
    { value: 'user', label: 'General Users' }
];

const getUserRole = (user) => {
    if (user.is_admin) {
        return { key: 'admin', label: 'Admin' };
    }

    if (user.is_doctor) {
        return { key: 'doctor', label: 'Doctor' };
    }

    if (user.is_laboratory) {
        return { key: 'laboratory', label: 'Laboratory' };
    }

    if (user.is_vendor) {
        return { key: 'vendor', label: 'Vendor' };
    }

    if (user.is_aht) {
        return { key: 'aht', label: 'AHT' };
    }

    return { key: 'user', label: 'User' };
};

const getDisplayName = (user) => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();

    if (fullName) {
        return fullName;
    }

    return user.username || user.email || `User ${user.id}`;
};

const getInitials = (user) => {
    const name = getDisplayName(user);
    const initials = name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('');

    return initials || 'U';
};

const formatDate = (value) => {
    if (!value) {
        return 'N/A';
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    return parsedDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const escapeCsvValue = (value) => {
    const normalizedValue = String(value ?? '').replace(/"/g, '""');
    return `"${normalizedValue}"`;
};

const resolveUsersResponse = (response) => {
    const payload = response;

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload)) {
        return payload.results;
    }

    if (Array.isArray(payload)) {
        return payload.data;
    }

    return [];
};

const AdminFarmers = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [filters, setFilters] = useState({
        status: 'all',
        role: 'all'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await usersApi.getUsers();
            setUsers(resolveUsersResponse(response));
        } catch (requestError) {
            setError(requestError.message || 'Failed to fetch users');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter((user) => {
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();
        const role = getUserRole(user);
        const matchesSearch = !normalizedSearchTerm || [
            getDisplayName(user),
            user.username,
            user.email,
            user.phone
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearchTerm));

        const matchesStatus =
            filters.status === 'all' ||
            (filters.status === 'active' ? user.is_active : !user.is_active);

        const matchesRole = filters.role === 'all' || role.key === filters.role;

        return matchesSearch && matchesStatus && matchesRole;
    });

    const exportData = () => {
        const header = ['Name', 'Username', 'Email', 'Phone', 'Role', 'Status', 'Date Joined'];
        const rows = filteredUsers.map((user) => {
            const role = getUserRole(user);

            return [
                getDisplayName(user),
                user.username || 'N/A',
                user.email || 'N/A',
                user.phone || 'N/A',
                role.label,
                user.is_active ? 'Active' : 'Inactive',
                formatDate(user.date_joined)
            ];
        });

        const csvContent = [header, ...rows]
            .map((row) => row.map(escapeCsvValue).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', 'eaquahealth-users.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Something went wrong</h2>
                <p>{error}</p>
                <Button
                    text="Try Again"
                    variant="primary"
                    onClick={fetchUsers}
                />
            </div>
        );
    }

    return (
        <div className="admin-farmers">
            <div className="page-header">
                <div className="header-content">
                    <h1>All Users</h1>
                    <p>{filteredUsers.length} of {users.length} platform users</p>
                </div>
                <Button
                    icon={<img src={ExportIcon} alt="" />}
                    text="Export Data"
                    variant="outline"
                    onClick={exportData}
                />
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <img src={Search} alt="" />
                    <input
                        type="text"
                        placeholder="Search users by name, email, username, or phone"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </div>

                <div className="filters">
                    <select
                        value={filters.status}
                        onChange={(event) => setFilters({ ...filters, status: event.target.value })}
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>

                    <select
                        value={filters.role}
                        onChange={(event) => setFilters({ ...filters, role: event.target.value })}
                    >
                        {ROLE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="summary-grid">
                <div className="summary-card">
                    <span className="summary-label">Active Users</span>
                    <strong>{users.filter((user) => user.is_active).length}</strong>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Inactive Users</span>
                    <strong>{users.filter((user) => !user.is_active).length}</strong>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Admins</span>
                    <strong>{users.filter((user) => user.is_admin).length}</strong>
                </div>
                <div className="summary-card">
                    <span className="summary-label">Service Accounts</span>
                    <strong>{users.filter((user) => user.is_doctor || user.is_laboratory || user.is_vendor || user.is_aht).length}</strong>
                </div>
            </div>

            <div className="users-list-card">
                {filteredUsers.length === 0 ? (
                    <div className="empty-state">
                        <h3>No users found</h3>
                        <p>Adjust the search term or filters to find a different set of users.</p>
                    </div>
                ) : (
                    <div className="users-table-wrapper">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Username</th>
                                    <th>Contact</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Date Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => {
                                    const role = getUserRole(user);

                                    return (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="user-avatar">{getInitials(user)}</div>
                                                    <div>
                                                        <div className="user-name">{getDisplayName(user)}</div>
                                                        <div className="user-email">{user.email || 'No email available'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{user.username || 'N/A'}</td>
                                            <td>
                                                <div className="contact-stack">
                                                    <span>{user.phone || 'No phone number'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`role-badge ${role.key}`}>{role.label}</span>
                                            </td>
                                            <td>
                                                <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{formatDate(user.date_joined)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminFarmers;